import { META_PIXEL_IDS } from 'lib/analytics/metaPixel';
import type { PageTier } from 'lib/consent/policy';
import {
  REDDIT_CLICK_ID_COOKIE,
  REDDIT_CLICK_ID_MAX_AGE_DAYS,
  REDDIT_CLICK_ID_PARAM,
  REDDIT_PIXEL_ID,
} from 'lib/analytics/redditPixel';
import { TRACKERS } from 'lib/analytics/trackers';
import {
  TRIPLE_WHALE_HOSTS,
  flushTripleWhaleProductView,
  tripleWhaleShop,
  tripleWhaleSnippet,
} from 'lib/analytics/tripleWhale';

const GTM_ID = TRACKERS.gtm.id;
const GOOGLE_ADS_ID = TRACKERS.googleAds.id;
const GA4_ID = TRACKERS.ga4.id;

type DataLayerWindow = Window & {
  dataLayer?: unknown[];
  fbq?: (...args: unknown[]) => void;
  ttq?: { page?: () => void };
};

function injectInline(id: string, code: string): void {
  if (!trackersLive() || document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.text = code;
  document.head.appendChild(script);
}

function injectSrc(id: string, src: string, attributes?: Record<string, string>): void {
  if (!trackersLive() || document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  if (attributes) {
    for (const [key, value] of Object.entries(attributes)) {
      script.setAttribute(key, value);
    }
  }
  document.head.appendChild(script);
}

/**
 * Live pixel ids only on production. Localhost stays off unless a consent
 * test opts in with `__UH_CONSENT_TEST__`.
 */
export function trackersLive(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  if (host === 'ultrahuman.com' || host === 'www.ultrahuman.com') return true;
  const testHost = host === 'localhost' || host === '127.0.0.1';
  return (
    testHost &&
    Boolean(
      (window as Window & { __UH_CONSENT_TEST__?: boolean | 'queue' })
        .__UH_CONSENT_TEST__,
    )
  );
}

/**
 * Install consent default, drop anything queued before consent, then start
 * gtm.js. Pre-consent pushes are not replayed.
 */
export function loadGtm(adsGranted: boolean): void {
  if (typeof window === 'undefined' || !trackersLive()) return;
  if (document.getElementById('uh-gtm')) return;
  const analyticsWindow = window as DataLayerWindow;
  analyticsWindow.dataLayer = [];
  const gtag = function gtag() {
    // GTM reads the Arguments object, not a rest-parameter array.
    // eslint-disable-next-line prefer-rest-params
    analyticsWindow.dataLayer?.push(arguments);
  } as NonNullable<Window['gtag']>;
  window.gtag = gtag;
  const storage = adsGranted ? 'granted' : 'denied';
  gtag('consent', 'default', {
    ad_storage: storage,
    // Enhanced conversions (email/phone matching) stay off until Legal assesses them.
    ad_user_data: 'denied',
    ad_personalization: storage,
    analytics_storage: 'granted',
  });
  analyticsWindow.dataLayer.push({
    'gtm.start': Date.now(),
    event: 'gtm.js',
  });
  injectSrc('uh-gtm', `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
}

/**
 * GA4 for pages where GTM cannot load because its container still holds ad
 * tags (health pages). Analytics only: ad storage, ad user data and ad
 * personalisation denied, no Google signals.
 */
export function loadGa4(): void {
  if (!trackersLive() || document.getElementById('uh-ga4')) return;
  const analyticsWindow = window as DataLayerWindow;
  analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // GTM reads the Arguments object, not a rest-parameter array.
      // eslint-disable-next-line prefer-rest-params
      analyticsWindow.dataLayer?.push(arguments);
    } as Window['gtag'];
  }
  const gtag = window.gtag;
  if (!gtag) return;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'granted',
  });
  gtag('js', new Date());
  gtag('config', GA4_ID, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  injectSrc('uh-ga4', `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`);
}

export function loadGoogleAds(): void {
  if (!trackersLive() || document.getElementById('uh-google-ads')) return;
  injectSrc(
    'uh-gtag-js',
    `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`,
  );
  const analyticsWindow = window as DataLayerWindow;
  analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? [];
  if (!window.gtag) {
    function gtag() {
      // GTM reads the Arguments object, not a rest-parameter array.
      // eslint-disable-next-line prefer-rest-params
      analyticsWindow.dataLayer?.push(arguments);
    }
    window.gtag = gtag as Window['gtag'];
  }
  const gtag = window.gtag;
  if (!gtag) return;
  gtag('js', new Date());
  gtag('config', GOOGLE_ADS_ID, {
    linker: {
      domains: [
        'ultrahuman.com',
        'checkout.in.ultrahuman.com',
        'checkout.euruk.ultrahuman.com',
        'checkout.global.ultrahuman.com',
        'checkout.uae.ultrahuman.com',
        'checkout.us.ultrahuman.com',
      ],
    },
  });
  const marker = document.createElement('script');
  marker.id = 'uh-google-ads';
  document.head.appendChild(marker);
}

/** Clarity needs an explicit consent signal for EEA/UK/CH visits; it only loads after analytics consent. */
export function loadClarity(adsGranted: boolean): void {
  injectInline(
    'uh-clarity',
    `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", ${JSON.stringify(TRACKERS.clarity.id)});clarity('consentv2',{ad_Storage:${JSON.stringify(adsGranted ? 'granted' : 'denied')},analytics_Storage:'granted'});`,
  );
}

/** `track_marketing` stores utm and click ids from the URL, so it follows ads consent. */
export function loadMixpanel(adsGranted: boolean): void {
  const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN ?? '';
  injectInline(
    'uh-mixpanel',
    `(function(f,b){if(!b.__SV){var e,g,i,h;window.mixpanel=b;b._i=[];b.init=function(e,f,c){function g(a,d){var b=d.split(".");2==b.length&&(a=a[b[0]],d=b[1]);a[d]=function(){a.push([d].concat(Array.prototype.slice.call(arguments,0)))}}var a=b;"undefined"!==typeof c?a=b[c]=[]:c="mixpanel";a.people=a.people||[];a.toString=function(a){var d="mixpanel";"mixpanel"!==c&&(d+="."+c);a||(d+=" (stub)");return d};a.people.toString=function(){return a.toString(1)+".people (stub)"};i="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking start_batch_senders people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");for(h=0;h<i.length;h++)g(a,i[h]);var j="set set_once union unset remove delete".split(" ");a.get_group=function(){function b(c){d[c]=function(){a.push([e,[c].concat(Array.prototype.slice.call(arguments,0))])}}for(var d={},e=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<j.length;c++)b(j[c]);return d};b._i.push([e,f,c])};b.__SV=1.2;e=f.createElement("script");e.type="text/javascript";e.async=!0;e.src="https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";g=f.getElementsByTagName("script")[0];g.parentNode.insertBefore(e,g)}}(document,window.mixpanel||[]));if(window.mixpanel){window.mixpanel.init(${JSON.stringify(token)},{debug:false,track_pageview:true,persistence:"localStorage",track_marketing:${adsGranted}});}`,
  );
}

/**
 * No automatic pushState PageViews (ConsentRuntime sends them for pages that
 * allow ads) and no automatic button/metadata collection.
 */
export function loadMeta(): void {
  const pixelId = JSON.stringify(META_PIXEL_IDS[0]);
  injectInline(
    'uh-meta',
    `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq.disablePushState=true;fbq('set','autoConfig',false,${pixelId});fbq('init',${pixelId});fbq('track','PageView');`,
  );
}

export function loadTikTok(): void {
  injectInline(
    'uh-tiktok',
    `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};ttq.load(${JSON.stringify(TRACKERS.tiktok.id)});ttq.page();}(window, document, 'ttq');`,
  );
}

export function loadReddit(): void {
  injectInline(
    'uh-reddit',
    `!function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init',${JSON.stringify(REDDIT_PIXEL_ID)},{optOut:false,useDecimalCurrencyValues:true});rdt('track','PageVisit');`,
  );
}

export function loadHubSpot(): void {
  injectSrc('uh-hubspot', `https://js.hs-scripts.com/${TRACKERS.hubspot.id}.js`);
}

const AD_SCRIPT_IDS = [
  'uh-gtm',
  'uh-google-ads',
  'uh-meta',
  'uh-tiktok',
  'uh-reddit',
  'uh-hubspot',
  'uh-wewomedia',
  'uh-triple-whale',
  'performance-lab-google-ads-config',
];
const ANALYTICS_SCRIPT_IDS = ['uh-mixpanel', 'uh-clarity', 'uh-ga4'];

/**
 * Third-party scripts stay on the page after an in-app navigation and keep
 * observing URL changes, so entering a stricter tier needs a fresh document.
 * Leaving a health page that loaded GA4 on its own also does, or GTM's GA4
 * would count the same visitor twice.
 */
export function needsFreshDocument(tier: PageTier): boolean {
  if (typeof document === 'undefined') return false;
  if (tier === 'marketing') return Boolean(document.getElementById('uh-ga4'));
  const blocked =
    tier === 'health' ? AD_SCRIPT_IDS : [...AD_SCRIPT_IDS, ...ANALYTICS_SCRIPT_IDS];
  return blocked.some((id) => document.getElementById(id));
}

function preconnect(href: string): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = href;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
  const dns = document.createElement('link');
  dns.rel = 'dns-prefetch';
  dns.href = href;
  document.head.appendChild(dns);
}

export function loadTripleWhale(store?: string | null): void {
  if (!trackersLive() || document.getElementById('uh-triple-whale')) {
    flushTripleWhaleProductView();
    return;
  }
  const shop = tripleWhaleShop(store);
  if (!shop) return;
  for (const host of TRIPLE_WHALE_HOSTS) preconnect(host);
  injectInline('uh-triple-whale', tripleWhaleSnippet(shop));
  flushTripleWhaleProductView();
}

export function loadWewomedia(): void {
  injectInline(
    'uh-wewomedia',
    `(function(){try{var s=document.createElement("script");s.async=true;s.src=(document.location.protocol=="https:"?"https:":"http:")+"//rtg.wewomedia.com/track/code.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(s,a);}catch(e){}})();`,
  );
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${maxAgeSeconds};SameSite=Lax${secure}`;
}

/** Click-id cookies written after ads consent. */
export function captureAdClickIds(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    if (fbclid) {
      const maxAge = 30 * 86400;
      writeCookie('_uh_fbclid', fbclid, maxAge);
      writeCookie('_fbc', `fb.1.${Date.now()}.${fbclid}`, maxAge);
    }
    const rdtCid = params.get(REDDIT_CLICK_ID_PARAM);
    if (rdtCid) {
      writeCookie(
        REDDIT_CLICK_ID_COOKIE,
        rdtCid,
        REDDIT_CLICK_ID_MAX_AGE_DAYS * 86400,
      );
    }
  } catch {
    // Attribution cookies must not break the page.
  }
}

// Cookie and storage name prefixes the loaded vendors write.
const AD_COOKIE_PREFIXES = [
  '_uh_fbclid',
  '_fbp',
  '_fbc',
  REDDIT_CLICK_ID_COOKIE,
  '_gcl_',
  '_ttp',
  '_tt_',
  'ttcsid',
  '_rdt_',
  '__hs',
  'hubspotutk',
  'messagesUtk',
  'li_fat_id',
  '_twclid',
];
const ANALYTICS_COOKIE_PREFIXES = ['_ga', '_gid', '_gat', '_clck', '_clsk', 'mp_'];
const AD_STORAGE_PREFIXES = ['TriplePixel', 'di_pmt_wt', '_tt', 'tt_'];
const ANALYTICS_STORAGE_PREFIXES = ['mp_', '__mpq_', '__mp_opt', 'ultrahuman_user_analytics'];

type VendorWindow = Window & {
  ttq?: { revokeConsent?: () => void };
  mixpanel?: { opt_out_tracking?: () => void };
  clarity?: (...args: unknown[]) => void;
  TriplePixel?: (...args: unknown[]) => void;
};

function expireCookies(prefixes: string[]): void {
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';
  // Vendors set some cookies on the parent domain so checkout.* can read them.
  const parts = window.location.hostname.split('.');
  const domains = ['', ...(parts.length > 1 ? [`;domain=.${parts.slice(-2).join('.')}`] : [])];
  for (const row of document.cookie.split('; ')) {
    const name = row.split('=')[0];
    if (!name || !prefixes.some((prefix) => name.startsWith(prefix))) continue;
    for (const domain of domains) {
      document.cookie = `${name}=;path=/;max-age=0;SameSite=Lax${domain}${secure}`;
    }
  }
}

function dropStorage(prefixes: string[]): void {
  try {
    for (const key of Object.keys(window.localStorage)) {
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // Storage can be blocked; nothing was kept there then.
  }
}

/**
 * Expire what vendors stored on this site for categories the visitor said no
 * to. Runs on every load with a stored no, because a vendor's unload beacon
 * can write a cookie again after the withdrawal clean-up.
 */
export function expireTrackerStorage(withdrawn: { ads: boolean; analytics: boolean }): void {
  if (typeof window === 'undefined') return;
  if (withdrawn.ads) {
    expireCookies(AD_COOKIE_PREFIXES);
    dropStorage(AD_STORAGE_PREFIXES);
  }
  if (withdrawn.analytics) {
    expireCookies(ANALYTICS_COOKIE_PREFIXES);
    dropStorage(ANALYTICS_STORAGE_PREFIXES);
  }
}

/**
 * Withdrawal: tell loaded vendors to stop, so nothing queued goes out on the
 * reload that follows, then expire what they stored on this site.
 */
export function forgetTrackerData(withdrawn: { ads: boolean; analytics: boolean }): void {
  if (typeof window === 'undefined') return;
  const w = window as VendorWindow;
  try {
    w.gtag?.('consent', 'update', {
      ...(withdrawn.ads
        ? { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' }
        : {}),
      ...(withdrawn.analytics ? { analytics_storage: 'denied' } : {}),
    });
    if (withdrawn.ads) {
      w.fbq?.('consent', 'revoke');
      w.ttq?.revokeConsent?.();
      w.TriplePixel?.('trackingConsent', false);
    }
    if (withdrawn.analytics) {
      w.mixpanel?.opt_out_tracking?.();
      w.clarity?.('stop');
    }
    expireTrackerStorage(withdrawn);
  } catch {
    // Vendor stubs can be half-loaded; the reload still stops them.
  }
}
